import React, { useId } from 'react'
import { motion } from 'motion/react'

// A row of flat-top honeycomb hexes. A droplet falls into the cell, the cell
// fills with honey from the bottom, the full cell turns one revolution in
// place, and the honey drains away for the next drop.
//
// Two modes:
//  - Indeterminate (the default, one hex): the whole cycle, on a loop. For
//    long agentic operations with no known stages. One hex with `current={1}`
//    is the same cell already filled and still — the finished mark, for work
//    being shown after it ended rather than as it happens.
//  - Phased (`total` + `current`): one hex per step. Completed hexes are
//    solid, the current one animates, upcoming ones are outline-only.
//
// The animation itself lives in styles/input.css — `.combfill-drop` for the
// falling droplet, `.combfill-honey` for the rising honey, and
// `.combfill-cell` for the revolution — including the reduced-motion
// fallback.
//
// The droplet is a sibling of the cell rather than a child: it must not turn
// with the hex, and it must not be clipped to it, since it starts above the
// viewBox and falls in.
//
// Color comes from `currentColor` — set the honey tone on the parent. The
// house tone is `text-honey`, the same gold the summaries use for a record
// streak (SLICE_COLORS[2], alongside the honey-bear bottles).
//
// Accessibility: the SVG is aria-hidden. The status phrase beside it is the
// accessible output, so `role="status"` belongs on the wrapper holding that
// text, never here.

// Flat-top hex, circumradius 10, drawn about the origin so the cell can spin
// about its own centre without knowing where on the row it sits. Spans 20
// wide x 17.32 tall.
const HEX = '10,0 5,8.66 -5,8.66 -10,0 -5,-8.66 5,-8.66'

// A teardrop about the same origin: a point at the top drawn down into a
// round bulb. Kept narrower and shorter than the hex so that when it lands it
// sits inside the outline rather than straddling it.
const DROP = 'M 0 -8 C 0 -8, 5.5 -1.5, 5.5 2.5 A 5.5 5.5 0 0 1 -5.5 2.5 C -5.5 -1.5, 0 -8, 0 -8 Z'

// How far the honey rect is pushed down to read as empty. Duplicated in the
// `combfill-rise` keyframes in styles/input.css, which can't see this; if one
// changes, change the other.
const RISE = 20

// Hex centres sit 26 apart: 20 of hex plus a 6 gap.
const PITCH = 26
const FIRST_CX = 12
const VIEWBOX_HEIGHT = 22

interface CombFillProps {
    /** Number of hexes. 1 (the default) is the indeterminate spinner. */
    total?: number
    /** Index of the animating hex; everything before it renders solid. */
    current?: number
    /** Rendered height in px. 16 is the floor at which the hex stays legible. */
    size?: number
    className?: string
}

const CombFill: React.FC<CombFillProps> = ({
    total = 1,
    current = 0,
    size = 20,
    className,
}) => {
    // Clip paths are referenced by id, so two spinners on one page need
    // distinct ones or the second steals the first's fill.
    const uid = useId().replace(/:/g, '')
    const width = 24 + PITCH * (total - 1)

    return (
        <svg
            className={className}
            height={size}
            width={(size * width) / VIEWBOX_HEIGHT}
            viewBox={`0 0 ${width} ${VIEWBOX_HEIGHT}`}
            aria-hidden="true"
            focusable="false"
        >
            {Array.from({ length: total }, (_, i) => {
                const id = `${uid}-${i}`
                // `current` is honoured at every `total`, including 1: a
                // lone cell with `current={1}` is the *finished* mark — solid,
                // still, no droplet — which is what a parse that has already
                // happened should look like. The default `current={0}` keeps
                // one cell animating, so the indeterminate spinner every other
                // call site asks for is unchanged.
                const state =
                    i < current ? 'done' : i === current ? 'active' : 'todo'

                return (
                    // The outer group places the cell on the row; the inner one
                    // spins about its own centre, which the outer translate has
                    // already put under the origin.
                    <g key={i} transform={`translate(${FIRST_CX + PITCH * i} 11)`}>
                        <g className={state === 'active' ? 'combfill-cell' : undefined}>
                            <clipPath id={id}>
                                <polygon points={HEX} />
                            </clipPath>

                            <polygon
                                points={HEX}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                // Heavier than they look like they should be:
                                // the honey gold is light, so at 16px on
                                // white the old 0.3/0.45 outline all but
                                // disappeared.
                                opacity={state === 'todo' ? 0.45 : 0.7}
                            />

                            {state === 'done' && <polygon points={HEX} fill="currentColor" />}

                            {state === 'active' && (
                                <g clipPath={`url(#${id})`}>
                                    <rect
                                        className="combfill-honey"
                                        x={-10}
                                        y={-VIEWBOX_HEIGHT / 2}
                                        width={20}
                                        height={VIEWBOX_HEIGHT}
                                        fill="currentColor"
                                    />
                                </g>
                            )}
                        </g>

                        {state === 'active' && (
                            <path className="combfill-drop" d={DROP} fill="currentColor" />
                        )}
                    </g>
                )
            })}
        </svg>
    )
}

export default CombFill

/** The rise on its own, driven by a countdown rather than a loop: one hex that
 *  fills from empty to full as an armed timer runs down, so the wait before an
 *  auto-parse looks like the parse it is about to become.
 *
 *  Module-level so it keeps its identity across the composer's renders --
 *  declared inline it would remount on every keystroke and restart the fill,
 *  which is the one thing this must not do.
 *
 *  Nothing ticks: the fill is a single linear animation from wherever the timer
 *  already is to full, re-run only when a new timer is armed (`key`). A
 *  remaining time of 0 leaves it full, which is what a just-fired timer should
 *  look like for the instant before it unmounts.
 */
export const CombFillCountdown: React.FC<{
    armedAt: number
    dueAt: number
    size?: number
}> = ({ armedAt, dueAt, size = 16 }) => {
    const uid = useId().replace(/:/g, '')
    const total = Math.max(dueAt - armedAt, 1)
    const remaining = Math.max(dueAt - Date.now(), 0)

    return (
        <svg
            className="shrink-0"
            height={size}
            width={(size * 24) / VIEWBOX_HEIGHT}
            viewBox={`0 0 24 ${VIEWBOX_HEIGHT}`}
            aria-hidden="true"
            focusable="false"
        >
            <g transform={`translate(${FIRST_CX} 11)`}>
                <clipPath id={uid}>
                    <polygon points={HEX} />
                </clipPath>

                <polygon
                    points={HEX}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity={0.7}
                />

                <g clipPath={`url(#${uid})`}>
                    {/* The translate rides a <g>, not the <rect>: `y` on a rect
                        is an SVG attribute as well as a transform, and motion
                        would have to guess which one is meant. */}
                    <motion.g
                        key={armedAt}
                        initial={{ y: (RISE * remaining) / total }}
                        animate={{ y: 0 }}
                        transition={{ duration: remaining / 1000, ease: 'linear' }}
                    >
                        <rect
                            x={-10}
                            y={-VIEWBOX_HEIGHT / 2}
                            width={20}
                            height={VIEWBOX_HEIGHT}
                            fill="currentColor"
                        />
                    </motion.g>
                </g>
            </g>
        </svg>
    )
}
